import { Flex, Text } from "@chakra-ui/react-v3";
import { Encounter } from "@medplum/fhirtypes";
import {
  isPractitioner,
  SmartCondition,
  SmartEncounter,
  SmartPractitioner,
} from "@metriport/fhir-sdk";
import { CodingBadge } from "../components/coding-badge";
import { ResourceCardLayout } from "../components/resource-card-layout";
import { getDocIdExtension } from "../utils/document-utils";
import { getDateForResourceType } from "../utils/resource-value-extractors";
import { getHccCoditionsForSidePanel } from "../../shared-components/fhir-sections/conditions/table-data";

export type ConditionTimelineItemProps = {
  condition: SmartCondition;
};

/**
 * Condition-specific timeline item that displays condition name, coding badges,
 * category badge, and facility/provider metadata.
 */
export function ConditionTimelineItem({ condition }: ConditionTimelineItemProps) {
  const date = getDateForResourceType(condition);
  const docId = getDocIdExtension(condition);

  // Prioritize ICD-10 display, then SNOMED, then any display, then text
  const conditionName =
    condition.code?.getIcd10()?.display ??
    condition.code?.getSnomed()?.display ??
    condition.code?.coding?.find(coding => coding.display)?.display ??
    condition.code?.text ??
    "-";
  const codings = condition.code?.coding?.filter(coding => coding.code && coding.system) ?? [];

  // Extract HCC codes from extensions
  const hccCodeableConcepts = getHccCoditionsForSidePanel(condition);
  const hccCodings = hccCodeableConcepts
    .flatMap(hcc => hcc.coding ?? [])
    .filter(coding => coding.code && coding.system);
  const allCodings = [...codings, ...hccCodings];

  const { facilityName, locationName, providerName } = getOccurrenceContext(condition);
  const facilityDisplay = facilityName ?? locationName;

  const categoryDisplay = getConditionCategoryDisplay(condition);
  const badgeColor = categoryDisplay === "Diagnosis" ? "blue" : "gray";

  if (!date) return null;

  return (
    <ResourceCardLayout
      date={date}
      docId={docId}
      resourceType="Condition"
      resources={[condition]}
      badge={
        categoryDisplay !== "-" ? { text: categoryDisplay, colorPalette: badgeColor } : undefined
      }
      facilityName={facilityDisplay}
      providerName={providerName}
    >
      {/* Condition name and codes */}
      {(conditionName !== "-" || allCodings.length > 0) && (
        <Flex gap={2} wrap="wrap" align="center">
          <Text fontSize="sm" fontWeight="medium" color="fg.emphasized">
            {conditionName}
          </Text>
          {allCodings.map((coding, index) => (
            <CodingBadge key={`${coding.system}-${coding.code}-${index}`} coding={coding} />
          ))}
        </Flex>
      )}
    </ResourceCardLayout>
  );
}

function getConditionCategoryDisplay(condition: SmartCondition): string {
  if (!condition.category || condition.category.length === 0) {
    return "-";
  }

  for (const category of condition.category) {
    if (category.hasLoincCode("75326-9")) {
      return "Problem List";
    }
    if (category.hasLoincCode("29308-4")) {
      return "Diagnosis";
    }

    // Fallback to text if available
    if (category.text && category.text !== "-") {
      return category.text;
    }
  }

  return "-";
}

type OccurrenceContext = {
  facilityName: string | undefined;
  locationName: string | undefined;
  providerName: string | undefined;
};

function getOccurrenceContext(condition: SmartCondition): OccurrenceContext {
  let facilityName: string | undefined;
  let locationName: string | undefined;
  let providerName: string | undefined;

  // Find the best encounter associated with this condition
  const encounter = findBestEncounterForCondition(condition);

  if (encounter) {
    // Get location from encounter - this is typically the facility/org
    const locations = encounter.getLocation();
    locationName = locations[0]?.name;

    // Get organization/facility from encounter's service provider
    const organization = encounter.getServiceProvider();
    facilityName = organization?.name;

    // Get practitioners from encounter participants
    const participants = encounter.getParticipants();
    const practitioners = participants.filter(isPractitioner);
    if (practitioners.length > 0) {
      providerName = formatPractitionerName(practitioners[0]);
    }
  }

  // Fallback: get provider from condition recorder or asserter
  if (!providerName) {
    const recorder = condition.getRecorder();
    const asserter = condition.getAsserter();

    if (recorder && isPractitioner(recorder)) {
      providerName = formatPractitionerName(recorder);
    } else if (asserter && isPractitioner(asserter)) {
      providerName = formatPractitionerName(asserter);
    }
  }

  return { facilityName, locationName, providerName };
}

/**
 * Find the best encounter associated with a condition.
 * Priority order:
 * 1. Encounters that reference this condition as a diagnosis (encounter.diagnosis.condition)
 * 2. Encounters that reference this condition as a reason (encounter.reasonReference)
 * 3. Forward reference from condition.encounter
 */
function findBestEncounterForCondition(condition: SmartCondition): SmartEncounter | undefined {
  // 1. Find encounters where this condition is listed as a diagnosis
  const diagnosisEncounters = condition.getReferencingResources<Encounter>({
    resourceType: "Encounter",
    referenceField: "diagnosis.condition",
  });

  if (diagnosisEncounters.length > 0) {
    return diagnosisEncounters[0] as SmartEncounter;
  }

  // 2. Find encounters where this condition is a reason for the encounter
  const reasonEncounters = condition.getReferencingResources<Encounter>({
    resourceType: "Encounter",
    referenceField: "reasonReference",
  });

  if (reasonEncounters.length > 0) {
    return reasonEncounters[0] as SmartEncounter;
  }

  // 3. Fallback to forward reference: condition.encounter -> Encounter
  return condition.getEncounter();
}

function formatPractitionerName(practitioner: SmartPractitioner | undefined): string | undefined {
  if (!practitioner?.name?.[0]) return undefined;

  const name = practitioner.name[0];
  const prefix = name.prefix?.join(" ") ?? "";
  const given = name.given?.join(" ") ?? "";
  const family = name.family ?? "";

  const fullName = [prefix, given, family].filter(Boolean).join(" ").trim();
  return fullName || undefined;
}
